/**
 * OwnerAccountsSection — provision/promote a multi-tenant owner (11F.3 / D9).
 *
 * Rendered only for `owner` callers (both the Settings nav-item visibility
 * AND this component's own guard — see the `user.role !== 'owner'` early
 * return below). The backend independently enforces this on
 * POST /v1/auth/owners (403 insufficient_role for anyone else); this is a
 * UX guard, not the security boundary, same relationship as TenantSwitcher
 * has to the server-side X-Tenant-Id check.
 */

import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Card, Button, Input } from '@/components/common';
import { useAuth } from '@/context';
import { SCHOOLS } from '@/constants/common';
import { useCreateOwnerAccount, type OwnerAccountResponse } from '@features/auth/api/owners';
import type { ApiError } from '@core/api/client';

const ALL_TENANT_OPTIONS = [
  { id: SCHOOLS.rhythmAndBeats.id, label: SCHOOLS.rhythmAndBeats.shortName },
  { id: SCHOOLS.humAndStrum.id, label: SCHOOLS.humAndStrum.shortName },
];

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 4px;
`;

const TenantCheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  padding: 6px 0;
  cursor: pointer;
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  margin-top: 2px;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ResultBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

const PasswordValue = styled.code`
  display: inline-block;
  margin-top: 4px;
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.surface};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

interface FormValues {
  email: string;
  display_name: string;
  tenant_ids: string[];
}

const EMPTY: FormValues = { email: '', display_name: '', tenant_ids: [] };

export const OwnerAccountsSection: React.FC = () => {
  const { user } = useAuth();
  // Only offer tenants the CALLING owner actually holds. The backend rejects
  // anything else with 403 tenant_not_authorized (granting a tenant you don't
  // hold is privilege escalation — see rbac.md), so listing all schools here
  // just invited a request that cannot succeed.
  // Fall back to the caller's own tenant, matching TenantContext — a token
  // without the `available_tenant_ids` claim still holds `tenant_id`, and
  // defaulting to [] there left the owner with an empty, unexplained list.
  const heldTenantIds = user?.available_tenant_ids ?? (user ? [user.tenant_id] : []);
  const grantableTenants = ALL_TENANT_OPTIONS.filter((t) => heldTenantIds.includes(t.id));
  const createMut = useCreateOwnerAccount();
  const [result, setResult] = useState<OwnerAccountResponse | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: EMPTY });

  // Server-side is the real boundary (require_role("owner")) — this just
  // keeps the form off the screen for anyone who'd get a 403 anyway.
  if (user?.role !== 'owner') return null;

  const onSubmit = handleSubmit(async (values) => {
    if (values.tenant_ids.length === 0) {
      setError('tenant_ids', { type: 'manual', message: 'Select at least one school' });
      return;
    }
    try {
      const created = await createMut.mutateAsync({
        email: values.email,
        display_name: values.display_name,
        tenant_ids: values.tenant_ids,
      });
      setResult(created);
      toast.success(
        created.action === 'created' ? 'Owner account created' : 'Existing account promoted to owner',
      );
      reset(EMPTY);
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 422 && Array.isArray(apiErr.details)) {
        (apiErr.details as { loc: (string | number)[]; msg: string }[]).forEach(({ loc, msg }) => {
          const field = loc[loc.length - 1] as keyof FormValues;
          if (field) setError(field, { type: 'server', message: msg });
        });
      } else {
        toast.error(apiErr.message || 'Failed to provision owner account');
      }
    }
  });

  return (
    <Card padding="lg" data-testid="candeza-owner-accounts-section">
      <h3 style={{ marginTop: 0 }}>Owner Accounts</h3>
      <p style={{ fontSize: 14, color: '#64748b', marginTop: 0 }}>
        Create a new owner, or promote an existing account, with access across the schools you
        select. Owner is the superuser role — only existing owners can grant it.
      </p>

      <form onSubmit={onSubmit} noValidate>
        <FormGrid>
          <div>
            <Input
              label="Email *"
              type="email"
              placeholder="owner@candeza.co"
              {...register('email', { required: 'Required' })}
              data-testid="candeza-owner-form-email-input"
            />
            {errors.email && <ErrorText>{errors.email.message}</ErrorText>}
          </div>
          <div>
            <Input
              label="Display Name *"
              placeholder="Full name"
              {...register('display_name', { required: 'Required' })}
              data-testid="candeza-owner-form-display-name-input"
            />
            {errors.display_name && <ErrorText>{errors.display_name.message}</ErrorText>}
          </div>
        </FormGrid>

        <div style={{ marginTop: 12 }}>
          <FieldLabel>Schools *</FieldLabel>
          <Controller
            control={control}
            name="tenant_ids"
            render={({ field }) => (
              <>
                {grantableTenants.map((tenant) => (
                  <TenantCheckboxRow key={tenant.id}>
                    <input
                      type="checkbox"
                      data-testid={`candeza-owner-form-tenant-${tenant.id}`}
                      checked={field.value.includes(tenant.id)}
                      onChange={(e) => {
                        field.onChange(
                          e.target.checked
                            ? [...field.value, tenant.id]
                            : field.value.filter((id) => id !== tenant.id),
                        );
                      }}
                    />
                    {tenant.label}
                  </TenantCheckboxRow>
                ))}
              </>
            )}
          />
          {errors.tenant_ids && <ErrorText>{errors.tenant_ids.message}</ErrorText>}
        </div>

        <FormActions>
          <Button type="submit" disabled={isSubmitting} data-testid="candeza-owner-form-submit-btn">
            {isSubmitting ? 'Saving...' : 'Provision Owner'}
          </Button>
        </FormActions>
      </form>

      {result && (
        <ResultBox data-testid="candeza-owner-form-result">
          <strong>{result.action === 'created' ? 'Created' : 'Promoted'}:</strong> {result.email} —
          schools: {result.tenant_ids.join(', ')}
          {result.generated_password && (
            <div>
              Temporary password (copy now — shown once):{' '}
              <PasswordValue>{result.generated_password}</PasswordValue>
            </div>
          )}
        </ResultBox>
      )}
    </Card>
  );
};

export default OwnerAccountsSection;
